import React, { useCallback, useState, useRef, useEffect } from 'react';
import { styled } from '@compiled/react';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { FEEDBACK_DISLIKE, FEEDBACK_LIKE } from '../../common/constants.tsx';
import { FeedbackButton } from '../../common/ui/feedback-button/index.tsx';
import { usePromptIsLoading, usePromptId } from '../../controllers/prompt-state/index.tsx';
import { NONE } from './constants.tsx';
import messages from './messages.tsx';
import type { FeedbackState, FeedbackType } from './types.tsx';

type LikeIconProps = {
	isSelected?: boolean;
	isDisabled?: boolean;
	label: string;
};

const LikeIcon = ({ isSelected = false, isDisabled = false, label }: LikeIconProps) => {
	const primaryColor = isSelected || isDisabled ? undefined : token('color.icon.accent.gray');
	return (
		<ThumbsUpIcon
			spacing="spacious"
			label={label}
			color={primaryColor}
			LEGACY_primaryColor={primaryColor}
		/>
	);
};

const DislikeIcon = (props: LikeIconProps) => (
	// TODO: after visual refresh is completely rolled out, change it to import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
	<Flip>
		<LikeIcon {...props} />
	</Flip>
);

/**
 * Render feedback controls, pre-wired to consume relevant context data from the AI data store. This component must be
 * rendered as child of `AIInlinePrompt` in order to receive required context data.
 *
 * @deprecated
 * This component is currently used by NL-to-JQL only. Other AI features should use the platform feedback component
 */
export const Feedback = () => {
	const { formatMessage } = useIntl();

	const [selected, setSelected] = useState<FeedbackState>(NONE);
	const hasOpened = useRef(false);

	const [isLoading] = usePromptIsLoading();
	const [promptId] = usePromptId();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		// Reset internal state whenever a new prompt has been submitted so more feedback can be given
		if (!isLoading) {
			setSelected(NONE);
			hasOpened.current = false;
		}
	}, [isLoading]);

	const onRating = useCallback(
		(feedbackType: FeedbackType) => (): boolean => {
			setSelected(feedbackType);
			// Only emit analytics event first time popup is opened
			if (!hasOpened.current) {
				fireTrackAnalytics(createAnalyticsEvent({}), 'aiFeedback submitted', {
					aiFeatureName: 'NL Search in Jira',
					singleInstrumentationID: promptId,
					proactiveAIGenerated: 0,
					userGeneratedAI: 1,
					isAIFeature: 1,
					aiFeedbackResult: feedbackType === 'LIKE' ? 'up' : 'down',
				});
			}
			hasOpened.current = true;
			return false;
		},
		[createAnalyticsEvent, promptId],
	);

	return (
		<Inline>
			<FeedbackButton
				appearance="subtle"
				iconBefore={
					<LikeIcon
						label={formatMessage(messages.jnlis645GoodResponse)}
						isSelected={selected === FEEDBACK_LIKE}
						isDisabled={isLoading}
					/>
				}
				isDisabled={isLoading}
				isPositive
				isSelected={selected === FEEDBACK_LIKE}
				onClick={onRating(FEEDBACK_LIKE)}
				offset={[40, 16]} // align with AI container box
				placement="bottom-end"
				spacing="compact"
				testId="atlassian-intelligence.ui.feedback.like-button"
				tooltipContent={selected === NONE && formatMessage(messages.jnlis645GoodResponse)}
				label={formatMessage(messages.jnlis645GoodResponse)}
			/>
			<FeedbackButton
				appearance="subtle"
				iconBefore={
					<DislikeIcon
						label={formatMessage(messages.jnlis645BadResponse)}
						isSelected={selected === FEEDBACK_DISLIKE}
						isDisabled={isLoading}
					/>
				}
				isDisabled={isLoading}
				isPositive={false}
				isSelected={selected === FEEDBACK_DISLIKE}
				onClick={onRating(FEEDBACK_DISLIKE)}
				offset={[8, 16]} // align with AI container box
				placement="bottom-end"
				spacing="compact"
				testId="atlassian-intelligence.ui.feedback.dislike-button"
				tooltipContent={selected === NONE && formatMessage(messages.jnlis645BadResponse)}
				label={formatMessage(messages.jnlis645BadResponse)}
			/>
		</Inline>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Flip = styled.span({
	transform: 'rotate(180deg)',
});
